import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Chip,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Divider,
  Badge,
} from "@mui/material";
import {
  WhatsApp as WhatsAppIcon,
  Refresh as RefreshIcon,
  LinkOff as LinkOffIcon,
  QrCode2 as QrCodeIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Sync as SyncIcon,
  Send as SendIcon,
  Inbox as InboxIcon,
  DeleteSweep as DeleteSweepIcon,
} from "@mui/icons-material";
import {
  whatsappService,
  type ConnectionStatus,
  type ConnectionState,
  type QueueInfo,
} from "../../../shared/services/whatsapp/whatsappService";

const WhatsAppConfig: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus | null>(null);
  const [currentState, setCurrentState] = useState<ConnectionState | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [disconnectDialog, setDisconnectDialog] = useState(false);
  const [qrCodeDialog, setQrCodeDialog] = useState(false);

  // Estado da fila de mensagens
  const [queueInfo, setQueueInfo] = useState<QueueInfo | null>(null);
  const [processingQueue, setProcessingQueue] = useState(false);
  const [cleaningQueue, setCleaningQueue] = useState(false);

  // Busca o estado atual da conexão
  const fetchCurrentState = useCallback(async () => {
    try {
      setLoading(true);
      const response = await whatsappService.getCurrentState();
      setCurrentState(response.data);
      setError(null);
    } catch (err: any) {
      console.error("Erro ao buscar estado:", err);
      setError(
        err?.response?.data?.message || "Erro ao buscar estado da conexão"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Busca o status via API do WhatsApp
  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await whatsappService.getStatus();
      setConnectionStatus(response.data);
      setError(null);
    } catch (err: any) {
      console.error("Erro ao buscar status:", err);
      setError(err?.response?.data?.message || "Erro ao verificar status");
    } finally {
      setLoading(false);
    }
  };

  // Conectar instância
  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);
      setSuccess(null);

      const response = await whatsappService.connect();
      setConnectionStatus(response.data);

      // Se retornou QR Code, mostrar dialog
      if (response.data.base64 || response.data.qrcode) {
        setQrCodeDialog(true);
      } else if (response.data.state === "open") {
        setSuccess("WhatsApp já está conectado!");
        await fetchCurrentState();
      }
    } catch (err: any) {
      console.error("Erro ao conectar:", err);
      setError(err?.response?.data?.message || "Erro ao conectar instância");
    } finally {
      setConnecting(false);
    }
  };

  // Desconectar instância
  const handleDisconnect = async () => {
    try {
      setLoading(true);
      setError(null);

      await whatsappService.disconnect();
      setSuccess("WhatsApp desconectado com sucesso!");
      setDisconnectDialog(false);
      await fetchCurrentState();
    } catch (err: any) {
      console.error("Erro ao desconectar:", err);
      setError(err?.response?.data?.message || "Erro ao desconectar instância");
    } finally {
      setLoading(false);
    }
  };

  // Buscar informações da fila
  const fetchQueue = useCallback(async () => {
    try {
      const response = await whatsappService.getQueue();
      setQueueInfo(response.data.queue);
    } catch (err: any) {
      console.error("Erro ao buscar fila:", err);
    }
  }, []);

  // Processar fila manualmente
  const handleProcessQueue = async () => {
    try {
      setProcessingQueue(true);
      setError(null);

      const response = await whatsappService.processQueue();
      const { result } = response.data;

      setSuccess(
        `Fila processada! ${result.processed} enviada(s), ${result.failed} falha(s), ${result.remaining} restante(s).`
      );
      await Promise.all([fetchQueue(), fetchCurrentState()]);
    } catch (err: any) {
      console.error("Erro ao processar fila:", err);
      setError(err?.response?.data?.message || "Erro ao processar fila");
    } finally {
      setProcessingQueue(false);
    }
  };

  // Limpar mensagens expiradas
  const handleCleanExpired = async () => {
    try {
      setCleaningQueue(true);
      setError(null);

      const response = await whatsappService.cleanExpiredMessages();
      setSuccess(response.data.message);
      await fetchQueue();
    } catch (err: any) {
      console.error("Erro ao limpar fila:", err);
      setError(
        err?.response?.data?.message || "Erro ao limpar mensagens expiradas"
      );
    } finally {
      setCleaningQueue(false);
    }
  };

  // Atualizar status
  const handleRefresh = async () => {
    await Promise.all([fetchCurrentState(), fetchStatus(), fetchQueue()]);
    setSuccess("Status atualizado!");
    setTimeout(() => setSuccess(null), 3000);
  };

  useEffect(() => {
    fetchCurrentState();
    fetchStatus();
    fetchQueue();
  }, [fetchCurrentState, fetchQueue]);

  // Auto-refresh quando QR Code dialog está aberto
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (qrCodeDialog) {
      interval = setInterval(async () => {
        try {
          const response = await whatsappService.getCurrentState();
          if (response.data.status === "connected") {
            setQrCodeDialog(false);
            setSuccess("WhatsApp conectado com sucesso!");
            setCurrentState(response.data);
          }
        } catch (err) {
          console.error("Erro no auto-refresh:", err);
        }
      }, 3000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [qrCodeDialog]);

  // Mapeia o state da API para status de exibição
  const getDisplayStatus = ():
    | "connected"
    | "disconnected"
    | "connecting"
    | "unknown" => {
    // Prioriza o connectionStatus.state (vem direto da API do WhatsApp)
    if (connectionStatus?.state) {
      if (connectionStatus.state === "open") return "connected";
      if (connectionStatus.state === "connecting") return "connecting";
      if (
        connectionStatus.state === "close" ||
        connectionStatus.state === "refused"
      )
        return "disconnected";
    }

    // Fallback para currentState (vem do webhook)
    if (currentState?.status) {
      return currentState.status;
    }

    return "unknown";
  };

  const getStatusChip = () => {
    const displayStatus = getDisplayStatus();

    const statusConfig = {
      connected: {
        color: "success" as const,
        icon: <CheckCircleIcon />,
        label: "Conectado",
      },
      disconnected: {
        color: "error" as const,
        icon: <ErrorIcon />,
        label: "Desconectado",
      },
      connecting: {
        color: "warning" as const,
        icon: <SyncIcon />,
        label: "Conectando...",
      },
      unknown: {
        color: "default" as const,
        icon: <WarningIcon />,
        label: "Desconhecido",
      },
    };

    const config = statusConfig[displayStatus] || statusConfig.unknown;

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        sx={{ fontWeight: 600, fontSize: 14 }}
      />
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("pt-BR");
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
          <WhatsAppIcon sx={{ fontSize: 40, color: "#25D366" }} />
          <Typography variant="h4" component="h1">
            Configuração do WhatsApp
          </Typography>
        </Box>

        {/* Alertas */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Card de Status */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6">Status da Conexão</Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {loading ? <CircularProgress size={20} /> : getStatusChip()}
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {loading && !currentState ? (
              <Box>
                <Skeleton variant="text" width="60%" height={30} />
                <Skeleton variant="text" width="40%" height={30} />
                <Skeleton variant="text" width="50%" height={30} />
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Instância
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {currentState?.instance ||
                      connectionStatus?.instance ||
                      "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status API
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {connectionStatus?.state || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Última Atualização
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(currentState?.lastUpdate)}
                  </Typography>
                </Grid>

                {currentState?.disconnectReason &&
                  getDisplayStatus() !== "connected" && (
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      <Typography variant="subtitle2">
                        Motivo da desconexão:
                      </Typography>
                      <Typography variant="body2">
                        {currentState.disconnectReason}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Card de Ações */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Ações
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={
                    connecting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <QrCodeIcon />
                    )
                  }
                  onClick={handleConnect}
                  disabled={connecting || getDisplayStatus() === "connected"}
                  sx={{ py: 1.5 }}
                >
                  {connecting ? "Conectando..." : "Conectar"}
                </Button>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{ py: 1.5 }}
                >
                  Atualizar Status
                </Button>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={() => setDisconnectDialog(true)}
                  disabled={loading || getDisplayStatus() !== "connected"}
                  sx={{ py: 1.5 }}
                >
                  Desconectar
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Card de Fila de Mensagens */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Badge
                  badgeContent={queueInfo?.total || 0}
                  color="warning"
                  max={99}
                >
                  <InboxIcon color="action" />
                </Badge>
                <Typography variant="h6">Fila de Mensagens</Typography>
              </Box>
              {queueInfo && queueInfo.total > 0 && (
                <Chip
                  label={`${queueInfo.total} pendente(s)`}
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            {queueInfo && queueInfo.total > 0 ? (
              <>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    ⚠️ Mensagens não enviadas durante a desconexão
                  </Typography>
                  <Typography variant="body2">
                    Existem <strong>{queueInfo.total}</strong> mensagem(ns) que
                    não foram enviadas enquanto o WhatsApp estava desconectado.
                    {getDisplayStatus() === "connected"
                      ? ' O WhatsApp está conectado agora. Clique em "Enviar Todas" para processar a fila.'
                      : " Conecte o WhatsApp primeiro para poder enviar as mensagens pendentes."}
                  </Typography>
                </Alert>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 2,
                        bgcolor: "background.default",
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="h4" color="primary" fontWeight={700}>
                        {queueInfo.total}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 2,
                        bgcolor: "background.default",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="info.main"
                        fontWeight={700}
                      >
                        {queueInfo.types.text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Texto
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 2,
                        bgcolor: "background.default",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        {queueInfo.oldest
                          ? new Date(queueInfo.oldest).toLocaleString("pt-BR")
                          : "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Mais Antiga
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      startIcon={
                        processingQueue ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SendIcon />
                        )
                      }
                      onClick={handleProcessQueue}
                      disabled={
                        processingQueue ||
                        cleaningQueue ||
                        getDisplayStatus() !== "connected"
                      }
                      sx={{ py: 1.5 }}
                    >
                      {processingQueue ? "Enviando..." : "Enviar Todas"}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      startIcon={
                        cleaningQueue ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <DeleteSweepIcon />
                        )
                      }
                      onClick={handleCleanExpired}
                      disabled={processingQueue || cleaningQueue}
                      sx={{ py: 1.5 }}
                    >
                      {cleaningQueue ? "Limpando..." : "Limpar Expiradas (24h)"}
                    </Button>
                  </Grid>
                </Grid>
              </>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CheckCircleIcon
                  sx={{ fontSize: 48, color: "success.main", mb: 1 }}
                />
                <Typography variant="body1" color="text.secondary">
                  Nenhuma mensagem pendente na fila
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Informação sobre funcionamento */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                📡 Como funciona a fila de mensagens
              </Typography>
              <Typography variant="body2" component="div">
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>
                    Quando o WhatsApp está <strong>desconectado</strong>, as
                    mensagens são armazenadas na fila.
                  </li>
                  <li>
                    Ao <strong>reconectar</strong>, você será notificado sobre
                    mensagens pendentes.
                  </li>
                  <li>
                    O envio das mensagens é <strong>manual</strong> - clique em
                    "Enviar Todas" para processar.
                  </li>
                  <li>
                    Mensagens com mais de <strong>24 horas</strong> são
                    consideradas expiradas.
                  </li>
                </ul>
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        {/* Dialog de QR Code */}
        <Dialog
          open={qrCodeDialog}
          onClose={() => setQrCodeDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: "center" }}>
            <QrCodeIcon sx={{ fontSize: 40, color: "#25D366", mb: 1 }} />
            <Typography variant="h6">Escaneie o QR Code</Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: "center", py: 2 }}>
              {connectionStatus?.base64 ? (
                <img
                  src={connectionStatus.base64}
                  alt="QR Code WhatsApp"
                  style={{
                    backgroundColor: "black",
                    maxWidth: "100%",
                    width: 300,
                    height: 300,
                    border: "2px solid #25D366",
                    borderRadius: 8,
                  }}
                />
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={60} sx={{ color: "#25D366" }} />
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Abra o WhatsApp no seu celular, vá em{" "}
                <strong>Configurações → Aparelhos conectados</strong> e escaneie
                o código.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mt: 2,
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Aguardando conexão...
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setQrCodeDialog(false)}>Fechar</Button>
            <Button
              variant="contained"
              onClick={handleConnect}
              startIcon={<RefreshIcon />}
            >
              Gerar Novo QR Code
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de Desconexão */}
        <Dialog
          open={disconnectDialog}
          onClose={() => setDisconnectDialog(false)}
        >
          <DialogTitle>Desconectar WhatsApp</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Ao desconectar, o sistema não poderá mais enviar mensagens até que
              você conecte novamente.
            </Alert>
            <Typography>
              Deseja realmente desconectar a instância do WhatsApp?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDisconnectDialog(false)}>Cancelar</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDisconnect}
              disabled={loading}
            >
              {loading ? "Desconectando..." : "Desconectar"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default WhatsAppConfig;
